import { permissionsByEntity, useCheckAccountType, useCheckPermissions } from '@cmg/auth';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import FlexLayout from '../../../../common/components/layout/flex-layout/FlexLayout';
import NotAccessible from '../../../../common/components/layout/not-accessible/NotAccessible';
import Spinner from '../../../../common/components/overlays/spinner/Spinner';
import { useFeatureToggles } from '../../../../common/config';
import { OfferingType } from '../../../../graphql';
import { useSideBar_OfferingDetails_Offering_BuySideQuery } from '../../components/side-bar/graphql';
import { useOfferingSidePanelOfferingId } from '../../hooks/useOfferingSidePanelMatch';
import FirmLevelDemand from './components/firm-level-demand/FirmLevelDemand';
import FundLevelDemand from './components/fund-level-demand/FundLevelDemand';
import { SRouteHeader, SVisibilityNote } from './MyInternalDemandRoute.styles';

const EMPTY_ARRAY = [];

const MyInternalDemandRoute: React.FC = () => {
  const offeringId = useOfferingSidePanelOfferingId();
  // just one permission is enough - specific permissions are checked in relevant components
  const canRead = useCheckPermissions(
    [permissionsByEntity.Ioi.READ, permissionsByEntity.FundIoi.READ],
    false
  );
  const isBuySideAccount = useCheckAccountType('BUY_SIDE');

  const { data, loading } = useSideBar_OfferingDetails_Offering_BuySideQuery({
    variables: { offeringId },
  });
  const [publishedOffering] = data?.publishedOfferings?.items ?? EMPTY_ARRAY;

  const isConvertibleOffering = publishedOffering?.type === OfferingType.Convertible;

  const isLinkedOffering = !!publishedOffering?.dataLabFull?.linkedOfferingId;
  const isExecutingOnPlatform = !!publishedOffering?.isExecutingOnPlatform;

  const { isOdsFunctionalityEnabled } = useFeatureToggles();

  if (loading) {
    return <Spinner show={true} fullHeight={true} />;
  }

  return !canRead ||
    !isBuySideAccount ||
    isConvertibleOffering ||
    (isOdsFunctionalityEnabled && isExecutingOnPlatform && !isLinkedOffering) ? (
    <NotAccessible subject="Internal Demand" />
  ) : (
    <FlexLayout>
      <SRouteHeader>Internal Demand</SRouteHeader>
      <SVisibilityNote>The following information is visible to your firm only</SVisibilityNote>
      <FirmLevelDemand
        offeringId={offeringId}
        linkedOfferingId={publishedOffering?.dataLabFull?.linkedOfferingId}
        offeringType={publishedOffering?.type}
      />
      <FundLevelDemand
        offeringId={offeringId}
        linkedOfferingId={publishedOffering?.dataLabFull?.linkedOfferingId}
        funds={defaultTo(publishedOffering?.dataLabFull?.funds, null)}
        fundIois={defaultTo(publishedOffering?.dataLabFull?.fundIois, null)}
      />
    </FlexLayout>
  );
};

export default MyInternalDemandRoute;
